// Attendees of a meeting: company members plus free-text guests. Shared by the
// import dialog and the record panel (BUILD 1 WP-4; moved out of meetings.tsx).
// Owner: WP-4.
import { useMemo, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import { WorkspaceIcon } from './icons';
import type { MeetingParticipantInput } from './meetings-api';
import type { CompanyMember } from './types';

/** Guests typed one per line or comma-separated; an address becomes the guest's e-mail. */
export function parseGuests(value: string): MeetingParticipantInput[] {
  return value
    .split(/[\n,]/u)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) =>
      entry.includes('@')
        ? { kind: 'guest' as const, name: entry.split('@')[0] || entry, email: entry }
        : { kind: 'guest' as const, name: entry },
    );
}

export function attendeeInputs(selected: string[], guests: string): MeetingParticipantInput[] {
  return [
    ...selected.map((userId) => ({ kind: 'member' as const, userId })),
    ...parseGuests(guests),
  ];
}

export function MeetingAttendeePicker({
  members,
  currentUserId,
  companyName,
  selected,
  onSelected,
  guests,
  onGuests,
}: {
  members: CompanyMember[];
  currentUserId: string | undefined;
  companyName: string;
  selected: string[];
  onSelected: (next: string[]) => void;
  guests: string;
  onGuests: (next: string) => void;
}) {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const [query, setQuery] = useState('');
  const ordered = useMemo(
    () =>
      [...members].sort((left, right) => {
        if (left.id === currentUserId) return -1;
        if (right.id === currentUserId) return 1;
        return left.displayName.localeCompare(right.displayName);
      }),
    [members, currentUserId],
  );
  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return ordered;
    return ordered.filter((member) =>
      `${member.displayName} ${member.email}`.toLocaleLowerCase().includes(needle),
    );
  }, [query, ordered]);
  const chosen = ordered.filter((member) => selected.includes(member.id));
  return (
    <>
      <fieldset>
        <legend>{formatCopy(copy.membersLabel, { company: companyName })}</legend>
        {!!chosen.length && (
          <div className="ws-member-chips">
            {chosen.map((member) => (
              <button
                type="button"
                key={member.id}
                onClick={() => onSelected(selected.filter((id) => id !== member.id))}
                aria-label={formatCopy(copy.removeMember, { name: member.displayName })}
              >
                {member.displayName}
                <WorkspaceIcon name="close" size={12} />
              </button>
            ))}
          </div>
        )}
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={copy.searchMembers}
          aria-label={copy.searchMembers}
        />
        <div className="ws-member-picker">
          {visible.length ? (
            visible.map((member) => (
              <label key={member.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(member.id)}
                  onChange={(event) =>
                    onSelected(
                      event.target.checked
                        ? [...new Set([...selected, member.id])]
                        : selected.filter((id) => id !== member.id),
                    )
                  }
                />
                <span>
                  {member.displayName}
                  {member.id === currentUserId ? ` ${copy.you}` : ''}
                  <small>
                    {member.email} · {member.role}
                  </small>
                </span>
              </label>
            ))
          ) : (
            <p className="ws-member-empty">{copy.noMatchingMembers}</p>
          )}
        </div>
      </fieldset>
      <label>
        {copy.guestsLabel} <span>{copy.optional}</span>
        <textarea
          value={guests}
          onChange={(event) => onGuests(event.target.value)}
          placeholder={copy.guestsPlaceholder}
        />
      </label>
    </>
  );
}
